/**
 * Embeddings layer — @xenova/transformers BGE-small-en-v1.5, with IndexedDB caching.
 *
 * Upgraded from MiniLM-L6-v2 on Day 8.5 for significantly better retrieval
 * quality on resume/JD matching (BGE-small is the current MTEB leader at
 * ≤100M parameters as of 2026). Same 384-dim output, same pipeline API,
 * strictly better scores on every ranking metric the benchmark measures.
 *
 * Runs entirely in the browser. First match pays ~40MB model download
 * (cached by @xenova in the browser's Cache API). Every subsequent match
 * is instant. Individual string embeddings are cached in IndexedDB keyed
 * by FNV-1a hash, so re-matching the same resume against a new JD reuses
 * the resume embedding.
 *
 * The Embedder protocol lets us swap for larger models (mpnet-base,
 * e5-base, etc.) without touching any scorer.
 */

import { hashText } from "./util.js";

export interface Embedder {
  readonly dim: number;
  readonly modelName: string;
  embed(text: string): Promise<number[]>;
  embedBatch(texts: string[]): Promise<number[][]>;
  warm(): Promise<void>;
}

// ---------------------------------------------------------------------------
// Xenova BGE-small-en-v1.5 — the default for Phase A (Day 8.5+)
// ---------------------------------------------------------------------------

const BGE_MODEL = "Xenova/bge-small-en-v1.5";
const BGE_DIM = 384;

// BGE models have a query/passage asymmetry — prepending an instruction
// to the query improves retrieval on asymmetric tasks. For resume/JD
// matching we treat the JD as the query and the resume as the passage.
// The instruction is documented by the BAAI/BGE authors.
const BGE_QUERY_INSTRUCTION =
  "Represent this sentence for searching relevant passages: ";

// Deferred import so this module loads cleanly in Node (for tests) even
// though the transformers library is browser-first.
type PipelineFn = (text: string[], opts: {
  pooling: "mean" | "none" | "cls";
  normalize: boolean;
}) => Promise<{ data: Float32Array }>;

let pipelinePromise: Promise<PipelineFn> | null = null;

async function getPipeline(): Promise<PipelineFn> {
  if (pipelinePromise) return pipelinePromise;
  pipelinePromise = (async () => {
    // eslint-disable-next-line @typescript-eslint/consistent-type-imports
    const transformers = await import("@xenova/transformers");
    // Prefer model files from the Xenova-hosted HF mirror; allow local
    // caching via the browser's Cache API which @xenova handles for us.
    transformers.env.allowLocalModels = false;
    transformers.env.useBrowserCache = true;
    const pipe = await transformers.pipeline("feature-extraction", BGE_MODEL);
    return pipe as unknown as PipelineFn;
  })();
  return pipelinePromise;
}

/**
 * Embed a single string with BGE, mean-pooled and L2-normalized.
 * Returns a 384-dim dense vector.
 */
async function runBge(text: string): Promise<number[]> {
  const pipe = await getPipeline();
  const result = await pipe([text], { pooling: "mean", normalize: true });
  return Array.from(result.data);
}

async function runBgeBatch(texts: string[]): Promise<number[][]> {
  const pipe = await getPipeline();
  const result = await pipe(texts, { pooling: "mean", normalize: true });
  // @xenova returns a flat Float32Array of shape (batch * dim). We reshape.
  const out: number[][] = [];
  for (let i = 0; i < texts.length; i++) {
    out.push(Array.from(result.data.slice(i * BGE_DIM, (i + 1) * BGE_DIM)));
  }
  return out;
}

// ---------------------------------------------------------------------------
// IndexedDB cache for text->embedding. Keyed by (model, hashText).
// ---------------------------------------------------------------------------
const DB_NAME = "talent-align-embeddings";
const DB_VERSION = 1;
const STORE = "cache";

async function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === "undefined") return null;
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function cacheGet(key: string): Promise<number[] | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, "readonly");
    const store = tx.objectStore(STORE);
    const req = store.get(key);
    req.onsuccess = () => {
      const val = req.result as number[] | undefined;
      resolve(val ?? null);
    };
    req.onerror = () => resolve(null);
  });
}

async function cacheSet(key: string, value: number[]): Promise<void> {
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}

// ---------------------------------------------------------------------------
// BgeEmbedder — Phase A default, as of Day 8.5
//
// Also exported as `MiniLMEmbedder` for backwards compatibility — every
// caller that imported MiniLMEmbedder still works, it just gets the BGE
// model under the hood now. Rename at leisure.
// ---------------------------------------------------------------------------
export class BgeEmbedder implements Embedder {
  readonly dim = BGE_DIM;
  readonly modelName = BGE_MODEL;

  async warm(): Promise<void> {
    // Loads the model + tokenizer into memory and primes the browser cache.
    await runBge("warm up the embedder");
  }

  async embed(text: string): Promise<number[]> {
    const key = `${this.modelName}:${hashText(text)}`;
    const cached = await cacheGet(key);
    if (cached) return cached;
    const vec = await runBge(text);
    await cacheSet(key, vec);
    return vec;
  }

  /**
   * BGE's asymmetric query/passage mode: prepends the instruction string
   * to the query text before embedding. Use this when embedding a JD
   * that you're matching against a pool of candidate resumes.
   */
  async embedQuery(text: string): Promise<number[]> {
    return this.embed(BGE_QUERY_INSTRUCTION + text);
  }

  async embedBatch(texts: string[]): Promise<number[][]> {
    // Look up each text in cache first; only embed the misses.
    const results: Array<number[] | null> = new Array(texts.length).fill(null);
    const missIndices: number[] = [];
    const missTexts: string[] = [];

    for (let i = 0; i < texts.length; i++) {
      const text = texts[i];
      if (text === undefined) continue;
      const key = `${this.modelName}:${hashText(text)}`;
      const cached = await cacheGet(key);
      if (cached) {
        results[i] = cached;
      } else {
        missIndices.push(i);
        missTexts.push(text);
      }
    }

    if (missTexts.length > 0) {
      const vecs = await runBgeBatch(missTexts);
      for (let j = 0; j < missTexts.length; j++) {
        const idx = missIndices[j];
        const vec = vecs[j];
        const text = missTexts[j];
        if (idx === undefined || !vec || text === undefined) continue;
        results[idx] = vec;
        await cacheSet(`${this.modelName}:${hashText(text)}`, vec);
      }
    }

    return results.map((r) => r ?? []);
  }
}

/** @deprecated Use BgeEmbedder. Alias preserved so existing imports work. */
export const MiniLMEmbedder = BgeEmbedder;
